import { z } from "zod";

/**
 * Guided signing (like DocuSign): the document as sent, with a box for each spot the customer has
 * to initial (every page, plus beside "Not included" and the payments) and one for the signature.
 * The customer taps each one; the server keeps the time of each tap, and signing needs them all.
 * The boxes come from the same code that draws the PDF, so they sit exactly on the lines.
 */

export type SignFieldKind = "initials" | "signature";

export interface SignField {
  /** Stable within the document: "p3" (page 3's footer), "excluded", "pay-2", "signature". */
  id: string;
  kind: SignFieldKind;
  /** 0-based page. */
  page: number;
  /** PDF points, origin bottom-left (as pdf-lib draws). */
  x: number;
  y: number;
  w: number;
  h: number;
  /** What it's for, for the customer and the certificate: "Page 2", "Not included", "Payment schedule". */
  label: string;
}

/** A tap the customer made, as the server recorded it. */
export interface SignTap {
  fieldId: string;
  at: string;
  ip: string;
}

/** What the signing page needs, besides the document. */
export interface SigningView {
  fields: SignField[];
  /** Field id → when it was tapped. */
  done: Record<string, string>;
  pageCount: number;
}

export const signTapSchema = z.object({ fieldId: z.string().min(1).max(40) });

/** Collects the boxes while a PDF is drawn (the drawing code calls `add`). */
export class SignFieldRecorder {
  readonly fields: SignField[] = [];
  add(f: SignField) {
    if (!this.fields.some((x) => x.id === f.id)) this.fields.push(f);
  }
}

/** The fields not tapped yet: the initials in reading order (page, then top to bottom), then the signature. */
export function remainingFields(fields: readonly SignField[], done: Record<string, string>): SignField[] {
  const rank = (f: SignField) => (f.kind === "signature" ? 1 : 0);
  return [...fields].filter((f) => !done[f.id]).sort((a, b) => rank(a) - rank(b) || a.page - b.page || b.y - a.y);
}

/** Why it can't be signed yet ("" when everything's done). The signature itself is tapped last. */
export function signingProblem(fields: readonly SignField[], done: Record<string, string>): string {
  const left = remainingFields(fields, done);
  const initials = left.filter((f) => f.kind === "initials").length;
  if (initials) return `Initial ${initials === 1 ? "1 more spot" : `${initials} more spots`} first.`;
  if (left.some((f) => f.kind === "signature")) return "Tap the signature box to sign.";
  return "";
}

/** The certificate's lines for the taps, in the order they happened. */
export function tapEvents(fields: readonly SignField[], taps: readonly SignTap[]): { label: string; at: Date; detail?: string }[] {
  return [...taps]
    .sort((a, b) => a.at.localeCompare(b.at))
    .map((t) => {
      const f = fields.find((x) => x.id === t.fieldId);
      const what = f?.kind === "signature" ? "Signed the signature box" : `Initialed ${f ? f.label : t.fieldId}${f && !/^Page /.test(f.label) ? ` (page ${f.page + 1})` : ""}`;
      return { label: what, at: new Date(t.at), detail: `from ${t.ip}` };
    });
}
